import { useCallback, useEffect, useRef, useState } from 'react';

import { type Environment, fetchQuery, graphql } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { PlatformSiteAri } from '@atlassian/ari/platform/site';

import type { useUnitLinkedResourcesQuery } from './__generated__/useUnitLinkedResourcesQuery.graphql';

const buildSiteAri = (cloudId: string): string =>
	PlatformSiteAri.create({ siteId: cloudId }).toString();

interface UnitLinkedResourcesState {
	error: boolean;
	/** All linked-resource ARIs returned for the current site's unit. */
	linkedResourceIds: string[];
	loading: boolean;
}

interface UseUnitLinkedResourcesResult extends UnitLinkedResourcesState {
	/**
	 * Returns `true` if the given `targetCloudId` belongs to the same
	 * collaboration-context / unit as the current cloudId.
	 *
	 * The check works by querying `admin_unitLinkedResource` for the current
	 * site ARI, then checking whether a site ARI containing
	 * the `targetCloudId` appears in the linked resources.
	 *
	 * **Fail-open behaviour:** Returns `true` (allow) when:
	 * - The feature gate is off
	 * - The query is still loading or errored
	 * - The target cloudId matches the current cloudId
	 * - The target ARI has no cloudId (e.g. identity-team)
	 */
	isCloudIdInSameUnit: (targetCloudId: string | undefined) => boolean;
}

/**
 * Pre-fetches the unit's linked resources for the current cloudId via the
 * `admin_unitLinkedResource` GraphQL query.
 */
export const useUnitLinkedResources = (
	relayEnvironment: Environment,
	cloudId: string,
): UseUnitLinkedResourcesResult => {
	// Initialise loading:true so the fail-open guard applies on the first render,
	// before the effect runs — otherwise a cross-unit cloudId would fail closed
	// against an empty linkedResourceIds. The effect resets loading immediately.
	const [state, setState] = useState<UnitLinkedResourcesState>({
		linkedResourceIds: [],
		loading: true,
		error: false,
	});

	// Mirror state in a ref so isCloudIdInSameUnit can read the latest values
	// without needing state in its dependency array (which would change its
	// identity on every query result and cause an infinite re-render loop).
	const stateRef = useRef(state);
	stateRef.current = state;

	useEffect(() => {
		if (!fg('preview_panel_unit_check') || !cloudId) {
			setState({ linkedResourceIds: [], loading: false, error: false });
			return;
		}

		// Use a local closure variable rather than a shared ref so that a stale
		// in-flight fetch resolving after a new effect run cannot pass the guard
		// and overwrite state with data for the old cloudId.
		let cancelled = false;
		setState({ linkedResourceIds: [], loading: true, error: false });

		const siteAri = buildSiteAri(cloudId);

		fetchQuery<useUnitLinkedResourcesQuery>(
			relayEnvironment,
			graphql`
				query useUnitLinkedResourcesQuery($id: ID!) {
					admin_unitLinkedResource(id: $id) {
						id
						links {
							edges {
								node {
									id
								}
							}
						}
					}
				}
			`,
			{ id: siteAri },
			{ fetchPolicy: 'store-or-network' },
		)
			.toPromise()
			.then((result) => {
				if (cancelled) {
					return;
				}
				const edges = result?.admin_unitLinkedResource?.links?.edges ?? [];
				const ids = edges
					.map((edge) => edge?.node?.id)
					.filter((id): id is string => typeof id === 'string');

				setState({ linkedResourceIds: ids, loading: false, error: false });
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				setState({ linkedResourceIds: [], loading: false, error: true });
			});

		return () => {
			cancelled = true;
		};
	}, [cloudId, relayEnvironment]);

	// Stable callback — reads from refs so its identity never changes,
	// preventing unnecessary re-renders of openPreviewPanel and its dependants.
	// stateRef.current is always up-to-date because we assign it on every render above.
	const isCloudIdInSameUnit = useCallback(
		(targetCloudId: string | undefined): boolean => {
			const { loading, error, linkedResourceIds } = stateRef.current;

			if (!fg('preview_panel_unit_check')) {
				return true;
			}

			// No target cloudId (e.g. identity-team) → allow
			if (!targetCloudId) {
				return true;
			}

			// Same cloudId → always allow
			if (targetCloudId === cloudId) {
				return true;
			}

			// Still loading or errored → fail-open (allow)
			if (loading || error) {
				return true;
			}

			return linkedResourceIds.includes(buildSiteAri(targetCloudId));
		},
		[cloudId],
	);

	return {
		...state,
		isCloudIdInSameUnit,
	};
};
